/**
 * Google Identity Services (OIDC) Authentication + Email OTP
 * Free unlimited authentication - no Firebase needed
 */

import { reactive } from 'vue'
import { setAuthToken } from './api.js'
import router from '@/router'
import { generateDeviceFingerprint, getStoredFingerprint, storeFingerprint } from './fingerprint.js'
import { setCurrentUserId, clearCurrentUserId, clearCurrentUserData } from './offlineQueue.js'
import { cache } from './cache.js'
import { clearNotesState } from '@/stores/notes.js'
import { clearTagsState } from '@/stores/tags.js'

const GOOGLE_CLIENT_ID = '122819501146-8efulvl8k2htdjvpic4qp3d9uls948t0.apps.googleusercontent.com'

// Reactive auth state
export const authState = reactive({
  currentUser: null,
  loading: true,
  error: null
})

let googleInitialized = false
let refreshTimer = null

/**
 * Initialize Google Identity Services
 */
export function initGoogleAuth() {
  return new Promise((resolve) => {
    // Load Google Identity Services script
    if (!document.getElementById('google-identity-script')) {
      const script = document.createElement('script')
      script.id = 'google-identity-script'
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = () => {
        initializeGoogleClient()
        resolve()
      }
      document.head.appendChild(script)
    } else {
      initializeGoogleClient()
      resolve()
    }
  })
}

/**
 * Initialize Google client
 */
async function initializeGoogleClient() {
  if (googleInitialized) return
  
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: handleCredentialResponse,
    auto_select: false,
    cancel_on_tap_outside: true
  })
  
  googleInitialized = true
  
  // Check for stored session
  const storedUser = localStorage.getItem('google_user')
  const accessToken = localStorage.getItem('access_token')
  
  if (storedUser && accessToken) {
    try {
      authState.currentUser = JSON.parse(storedUser)
      setAuthToken(accessToken)
      
      // Set userId for IndexedDB filtering
      setCurrentUserId(authState.currentUser.uid)
      
      // Fetch admin status
      fetchAdminStatus().catch(console.error)
      
      // Try to refresh immediately to get fresh token
      const fingerprint = getStoredFingerprint()
      if (fingerprint) {
        try {
          const response = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ deviceFingerprint: fingerprint })
          })
          
          if (response.ok) {
            const data = await response.json()
            localStorage.setItem('access_token', data.accessToken)
            setAuthToken(data.accessToken)
            setCurrentUserId(authState.currentUser.uid)
            scheduleTokenRefresh(data.expiresIn)
          } else {
            // Refresh failed, clear session
            console.log('Token refresh failed, clearing session')
            localStorage.clear()
            authState.currentUser = null
            setAuthToken(null)
            clearCurrentUserId()
          }
        } catch (error) {
          console.error('Token refresh error:', error)
          localStorage.clear()
          authState.currentUser = null
          setAuthToken(null)
          clearCurrentUserId()
        }
      } else {
        // No fingerprint, clear session
        console.log('No device fingerprint found, clearing session')
        localStorage.clear()
        authState.currentUser = null
        setAuthToken(null)
        clearCurrentUserId()
      }
    } catch (e) {
      console.error('Session restore error:', e)
      localStorage.clear()
      authState.currentUser = null
      setAuthToken(null)
      clearCurrentUserId()
    }
  }
  
  authState.loading = false
}

/**
 * Handle credential response from Google
 * This is called by the Google button - we'll process it differently
 */
async function handleCredentialResponse(response) {
  authState.loading = true
  authState.error = null
  
  try {
    const credential = response.credential
    
    // Store the credential temporarily for the login page to process
    window._pendingGoogleCredential = credential
    
    // Decode JWT to get user info for display
    const userInfo = parseJwt(credential)
    authState.tempUserInfo = {
      uid: userInfo.sub,
      email: userInfo.email,
      displayName: userInfo.name,
      photoURL: userInfo.picture,
      emailVerified: userInfo.email_verified
    }
    
  } catch (error) {
    console.error('Error handling credential:', error)
    authState.error = 'Authentication failed'
  } finally {
    authState.loading = false
  }
}

/**
 * Complete Google login with remember device option
 */
export async function completeGoogleLogin(rememberDevice) {
  authState.loading = true
  authState.error = null
  
  try {
    const credential = window._pendingGoogleCredential
    if (!credential) {
      throw new Error('No pending Google credential')
    }

    // Always generate or retrieve device fingerprint
    let deviceFingerprint = getStoredFingerprint() || await generateDeviceFingerprint()
    storeFingerprint(deviceFingerprint)

    // Call backend to complete login
    const response = await fetch('/api/auth/google-login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        idToken: credential,
        rememberDevice,
        deviceFingerprint
      })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Login failed')
    }

    const data = await response.json()

    // Store user info
    const user = {
      uid: data.user?.uid,
      email: data.user?.email,
      displayName: data.user?.name,
      photoURL: data.user?.picture,
      emailVerified: true
    }

    authState.currentUser = user
    localStorage.setItem('google_user', JSON.stringify(user))

    // Always using our token system now
    if (data.accessToken) {
      localStorage.setItem('access_token', data.accessToken)
      setAuthToken(data.accessToken)
      
      // Set userId for IndexedDB filtering
      setCurrentUserId(user.uid)
      
      // Setup auto-refresh before expiry
      scheduleTokenRefresh(data.expiresIn)
    } else {
      throw new Error('Invalid response from server')
    }

    // Clean up
    delete window._pendingGoogleCredential
    delete authState.tempUserInfo

    // Redirect to app
    router.push('/notes')
    
    // Fetch admin status
    fetchAdminStatus().catch(console.error)
    
  } catch (error) {
    console.error('Complete Google login error:', error)
    authState.error = error.message || 'Authentication failed'
    throw error
  } finally {
    authState.loading = false
  }
}

/**
 * Schedule automatic token refresh
 */
function scheduleTokenRefresh(expiresIn) {
  // Clear any existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer)
  }
  
  // Refresh 2 minutes before expiry (or immediately if less than 2 min left)
  const refreshTime = Math.max(0, (expiresIn - 120) * 1000)
  
  refreshTimer = setTimeout(async () => {
    try {
      const fingerprint = getStoredFingerprint()
      if (!fingerprint) {
        console.error('No device fingerprint for refresh')
        logout()
        router.push('/login')
        return
      }

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          deviceFingerprint: fingerprint
        })
      })

      if (!response.ok) {
        throw new Error('Token refresh failed')
      }

      const data = await response.json()
      localStorage.setItem('access_token', data.accessToken)
      setAuthToken(data.accessToken)
      
      // Ensure userId is set
      if (authState.currentUser?.uid) {
        setCurrentUserId(authState.currentUser.uid)
      }
      
      // Schedule next refresh
      scheduleTokenRefresh(data.expiresIn)
    } catch (error) {
      console.error('Token refresh error:', error)
      logout()
      router.push('/login')
    }
  }, refreshTime)
}

/**
 * Parse JWT token
 */
function parseJwt(token) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )
  return JSON.parse(jsonPayload)
}

/**
 * Sign in with Google
 */
export async function loginWithGoogle() {
  authState.loading = true
  authState.error = null
  
  try {
    // Prompt Google One Tap or redirect to login
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        console.log('One Tap not shown, use button instead')
      }
    })
  } catch (error) {
    console.error('Login error:', error)
    authState.error = 'Login failed. Please try again.'
    authState.loading = false
    throw error
  }
}

/**
 * Render Google Sign-In button
 */
export function renderGoogleButton(elementId, options = {}) {
  if (!googleInitialized) {
    console.error('Google Auth not initialized')
    return
  }
  
  const defaultOptions = {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'signin_with',
    shape: 'rectangular',
    logo_alignment: 'left',
    width: 300
  }
  
  window.google.accounts.id.renderButton(
    document.getElementById(elementId),
    { ...defaultOptions, ...options }
  )
}

/**
 * Send OTP to email
 */
export async function loginWithEmail(email) {
  authState.loading = true
  authState.error = null
  
  try {
    // Call your backend API to send OTP
    const response = await fetch('/api/auth/send-otp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    })
    
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to send OTP')
    }
    
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Send OTP error:', error)
    authState.error = error.message || 'Failed to send OTP'
    throw error
  } finally {
    authState.loading = false
  }
}

/**
 * Verify email OTP
 */
export async function verifyEmailOTP(email, otp) {
  authState.loading = true
  authState.error = null
  
  try {
    // Call your backend API to verify OTP
    const response = await fetch('/api/auth/verify-otp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, otp })
    })
    
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Invalid OTP')
    }
    
    const data = await response.json()
    
    // Create user object from email login
    const user = {
      uid: data.uid || email, // Use backend-provided UID or email
      email: email,
      displayName: email.split('@')[0], // Use email prefix as display name
      photoURL: null,
      emailVerified: true
    }
    
    // Store user and token
    authState.currentUser = user
    localStorage.setItem('google_user', JSON.stringify(user))
    localStorage.setItem('google_token', data.token)
    
    // Set token for API calls
    setAuthToken(data.token)
    
    // Set userId for IndexedDB filtering
    setCurrentUserId(user.uid)
    
    return data
  } catch (error) {
    console.error('Verify OTP error:', error)
    authState.error = error.message || 'Invalid OTP'
    throw error
  } finally {
    authState.loading = false
  }
}

/**
 * Sign out
 */
export async function logout() {
  authState.loading = true
  authState.error = null
  
  try {
    // Clear refresh timer
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }

    // SECURITY CRITICAL: Clear all user data - this must NEVER fail
    // Order matters: Clear data first, then tokens
    
    // Step 1: Clear IndexedDB data for current user (CRITICAL)
    try {
      await clearCurrentUserData()
    } catch (error) {
      console.error('CRITICAL: Failed to clear IndexedDB data:', error)
      // Don't throw - continue with other clearing steps
    }
    
    // Step 2: Clear in-memory state (CRITICAL)
    try {
      clearNotesState()
      clearTagsState()
    } catch (error) {
      console.error('CRITICAL: Failed to clear in-memory state:', error)
    }
    
    // Step 3: Clear localStorage cache
    try {
      cache.clearAll()
    } catch (error) {
      console.error('Failed to clear cache:', error)
    }
    
    // Step 4: Notify backend (optional - can fail)
    const accessToken = localStorage.getItem('access_token')
    if (accessToken) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include'
        })
      } catch (error) {
        console.error('Logout API error:', error)
      }
    }
    
    // Step 5: Clear localStorage auth data
    try {
      localStorage.removeItem('google_user')
      localStorage.removeItem('access_token')
      localStorage.removeItem('device_fingerprint')
      // Also clear google_token if exists
      localStorage.removeItem('google_token')
    } catch (error) {
      console.error('Failed to clear localStorage:', error)
    }
    
    // Step 6: Revoke Google session
    if (window.google?.accounts?.id) {
      try {
        window.google.accounts.id.disableAutoSelect()
      } catch (error) {
        console.error('Failed to revoke Google session:', error)
      }
    }
    
    // Step 7: Clear auth state and userId
    setAuthToken(null)
    authState.currentUser = null
    clearCurrentUserId()
    
    // Step 8: Clear admin cache
    adminCheckCache = null
    adminCheckPromise = null
    
  } catch (error) {
    console.error('CRITICAL: Logout error:', error)
    authState.error = 'Logout failed. Please try again.'
    throw error
  } finally {
    authState.loading = false
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return !!authState.currentUser
}

/**
 * Check if current user is admin (cached)
 */
let adminCheckCache = null
let adminCheckPromise = null

export function isAdmin() {
  return adminCheckCache === true
}

/**
 * Fetch admin status from backend
 */
export async function fetchAdminStatus() {
  if (adminCheckPromise) return adminCheckPromise
  
  adminCheckPromise = (async () => {
    try {
      const response = await fetch('/api/admin/check', {
        headers: {
          'Authorization': `Bearer ${getCurrentToken()}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        adminCheckCache = data.isAdmin
        return data.isAdmin
      }
    } catch (error) {
      console.error('Admin check error:', error)
    }
    
    adminCheckCache = false
    return false
  })()
  
  return adminCheckPromise
}

/**
 * Get current token
 */
export function getCurrentToken() {
  return localStorage.getItem('access_token')
}
